import React, { ChangeEventHandler, VFC } from 'react'
import styled, { css } from 'styled-components'

import { Checkbox, CheckboxWrapperLabel } from '@Components/FormElements/UI/Checkbox'
import { ImageV2 } from '@Components/Image'
import { CrossSellProduct } from '@Globals/CMS/ProductHelpers'
import { blackImageOverlay } from '@Globals/Style/mixins'
import theme from '@Globals/Style/theme'
import { useTranslation } from '@Globals/Translations'

type CrossSellItemProps = {
  onChange?: ChangeEventHandler<HTMLInputElement>
  product?: CrossSellProduct
  selected?: boolean
}

const CrossSellProductWrapper = styled.article(() => css`
  font-size: 0.8rem;
  height: 100%;
  padding-bottom: 30px;
  position: relative;
  user-select: none;
  width: 100%;

  * {
    font-size: inherit;
    vertical-align: bottom;
  }

  ${CheckboxWrapperLabel} {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }
`)

const ProductNameWrapper = styled.div({
  '-webkit-box-orient': 'vertical',
  '-webkit-line-clamp': '2',
  display: '-webkit-box',
  marginBottom: theme.padding.phablet,
  marginTop: theme.padding.phablet,
  overflow: 'hidden',

  'a:hover &': {
    textDecoration: 'underline',
  },
})

const BlackOverlay = styled.div`
  position: absolute;
  width: 100px;
  height: 100px;
  z-index: 10;
  ${blackImageOverlay};
  pointer-events: none;
`

const ProductLink = styled.a(({
  display: 'block',
}))

export const CrossSellItem: VFC<CrossSellItemProps> = ({ onChange, product, selected }) => {
  const { t } = useTranslation()

  if (!product) {
    return null
  }

  const { id, masterVariant, name, primaryAsset, url } = product

  const renderedImage = primaryAsset?.url && (
    <ImageV2 alt={name || ''} height={100} src={primaryAsset.url} width={100} />
  )

  const renderedName = <ProductNameWrapper>{name}</ProductNameWrapper>

  const label = t('products:ALSO_ADD_PRODUCT_TO_CART', { arg1: name })

  return (
    <CrossSellProductWrapper key={id} title={name || ''}>
      <BlackOverlay />

      {url
        ? (
          <ProductLink href={url}>
            {renderedImage}
            {renderedName}
          </ProductLink>
        )
        : (
          <>
            {renderedImage}
            {renderedName}
          </>
        )}

      <Checkbox
        aria-label={label}
        checked={selected}
        id={`crossSell${id}`}
        indicatorClassName="pdp-checkbox"
        label={`+ ${masterVariant?.price?.format}`}
        onChange={onChange}
        title={label}
        value={id}
      />
    </CrossSellProductWrapper>
  )
}
